import { WIDGET_SHADOW_ROOT_ENABLED_DATA_ATTR } from "@/constants";
import { REWARDS_WIDGET_NAMES } from "@/constants/widget-names";
import { RewardsLoyaltyInfoPageWidgetDefinition } from "@apps/rewards/constants/definitions";
import type { RewardsWidgetInjectorConfig } from "@apps/rewards/types";
import WidgetInjectedSdkEvent from "@modules/sdk/common_sdk/events/WidgetInjectedSdkEvent";
import { dispatchSdkEvent } from "@modules/sdk/helpers";
import {
    parseOptionalBooleanDataAttr,
    parseStringDataAttr,
} from "@ui-utils/Injector/utils";

const CUSTOM_REDEEM_LINK_DATA_ATTR = "gw-custom-redeem-link";
const CUSTOM_POINTS_IMG_URL_DATA_ATTR = "gw-custom-points-img-url";
const CUSTOM_REFERRAL_IMG_URL_DATA_ATTR = "gw-custom-referral-img-url";
const CUSTOM_VIP_TIERS_IMG_URL_DATA_ATTR = "gw-custom-vip-tiers-img-url";

class RewardsLoyaltyInfoPageWidgetInjector {
    constructor(private readonly config: RewardsWidgetInjectorConfig) {}

    start(): void {
        this.config.logger.debug("RewardsLoyaltyInfoPageWidgetInjector start", {
            placeholderSelector: this.config.placeholderSelector,
        });

        const widgetName =
            REWARDS_WIDGET_NAMES.REWARDS_LOYALTY_INFO_PAGE_WIDGET;

        const targets$ = this.config.mutationObserverInjector.register({
            targetSelector: this.config.placeholderSelector,
        });

        targets$.subscribe((placeholder) => {
            this.config.logger?.debug(
                "RewardsLoyaltyInfoPageWidgetInjector found placeholder",
                {
                    placeholder,
                }
            );

            void (async () => {
                const RewardsLoyaltyInfoPageWidget =
                    await this.config.dynamicImportManager.importDefault(
                        RewardsLoyaltyInfoPageWidgetDefinition,
                        true
                    );

                const widget = new RewardsLoyaltyInfoPageWidget(
                    {
                        target: placeholder,
                        customRedeemLinkUrl: parseStringDataAttr(
                            placeholder,
                            CUSTOM_REDEEM_LINK_DATA_ATTR
                        ),
                        customPointsImgUrl: parseStringDataAttr(
                            placeholder,
                            CUSTOM_POINTS_IMG_URL_DATA_ATTR
                        ),
                        customReferralImgUrl: parseStringDataAttr(
                            placeholder,
                            CUSTOM_REFERRAL_IMG_URL_DATA_ATTR
                        ),
                        customVipTiersImgUrl: parseStringDataAttr(
                            placeholder,
                            CUSTOM_VIP_TIERS_IMG_URL_DATA_ATTR
                        ),
                        isWidgetShadowRootEnabled: parseOptionalBooleanDataAttr(
                            placeholder,
                            WIDGET_SHADOW_ROOT_ENABLED_DATA_ATTR
                        ),
                    },
                    this.config.logger,
                    this.config.dynamicImportManager
                );

                await widget.init();

                this.config.eventsCollector.pushEvent(
                    this.config.eventsFactory.createWidgetInjected(widgetName)
                );

                dispatchSdkEvent(new WidgetInjectedSdkEvent(widgetName));
            })();
        });
    }
}

export default RewardsLoyaltyInfoPageWidgetInjector;
